import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type NamedColor = { name: string; hex: string };

export function ColorPickerBox({ namedColors }: { namedColors?: NamedColor[] }) {
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const [hue, setHue] = useState<number>(200);
  const [sat, setSat] = useState<number>(100);
  const [light, setLight] = useState<number>(50);
  const [dragging, setDragging] = useState(false);

  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  const hslToHex = useCallback((h: number, s: number, l: number) => {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const r = Math.round(255 * f(0));
    const g = Math.round(255 * f(8));
    const b = Math.round(255 * f(4));
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  }, []);

  const currentHex = useMemo(() => hslToHex(hue, sat, light), [hue, sat, light, hslToHex]);

  const hexToRgb = (hex: string) => {
    const m = hex.replace("#", "");
    const r = parseInt(m.substring(0, 2), 16);
    const g = parseInt(m.substring(2, 4), 16);
    const b = parseInt(m.substring(4, 6), 16);
    return { r, g, b };
  };

  const nearestNamed = useMemo(() => {
    if (!namedColors || namedColors.length === 0) return null;
    const { r: r0, g: g0, b: b0 } = hexToRgb(currentHex);
    let best: { name: string; hex: string; d2: number } | null = null;
    for (const c of namedColors) {
      const { r, g, b } = hexToRgb(c.hex);
      const d2 = (r - r0) ** 2 + (g - g0) ** 2 + (b - b0) ** 2;
      if (!best || d2 < best.d2) best = { name: c.name, hex: c.hex, d2 };
    }
    return best ? { name: best.name, hex: best.hex } : null;
  }, [namedColors, currentHex]);

  const clamp = (v: number, min = 0, max = 100) => Math.max(min, Math.min(max, v));

  const updateFromPoint = useCallback((clientX: number, clientY: number) => {
    const el = pickerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clamp(((clientX - rect.left) / rect.width) * 100);
    const y = clamp(((clientY - rect.top) / rect.height) * 100);
    setSat(Math.round(x));
    setLight(Math.round(100 - y));
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    updateFromPoint(e.clientX, e.clientY);
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragging) return;
    updateFromPoint(e.clientX, e.clientY);
  };
  const onMouseUp = () => setDragging(false);
  const onMouseLeave = () => setDragging(false);

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setDragging(true);
    const t = e.touches[0];
    updateFromPoint(t.clientX, t.clientY);
  };
  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const t = e.touches[0];
    updateFromPoint(t.clientX, t.clientY);
  };
  const onTouchEnd = () => setDragging(false);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">
      <div
        ref={pickerRef}
        className="relative w-full aspect-square rounded border border-gray-300 cursor-crosshair select-none"
        style={{
          backgroundColor: `hsl(${hue}, 100%, 50%)`,
          backgroundImage:
            `linear-gradient(to right, #fff, rgba(255,255,255,0)),` +
            `linear-gradient(to top, #000, rgba(0,0,0,0))`,
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        aria-label="Saturation and Lightness selector"
      >
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${sat}%`, top: `${100 - light}%` }}
        />
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-sm text-gray-600">Hue: {hue}°</label>
        <input
          type="range"
          min={0}
          max={360}
          value={hue}
          onChange={(e) => setHue(parseInt(e.target.value, 10))}
          className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-cyan-500 via-blue-500 via-purple-500 to-red-500"
          aria-label="Hue slider"
        />
        <div className="flex items-center gap-3 mt-2">
          <div className="w-10 h-10 rounded border" style={{ backgroundColor: currentHex }} />
          <div>
            <div className="font-mono text-sm">{currentHex}</div>
            {nearestNamed && (
              <div className="text-xs text-gray-600">≈ {nearestNamed.name} ({nearestNamed.hex})</div>
            )}
            <div className="text-xs text-gray-500">HSL({hue}, {sat}%, {light}%)</div>
          </div>
          <button
            className="ml-auto px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            onClick={() =>
              navigator.clipboard
                .writeText(currentHex)
                .then(() => toast.success(`${currentHex} copied`))
                .catch(() => toast.error("Copy failed"))
            }
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}
